import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft as ArrowLeftIcon, Printer as PrinterIcon } from 'lucide-react';
import pacientesService from '../../services/pacientesService';
import medicoVfpService, { DoctorInfo } from '../../services/medicoVfpService';
import {
  DEFAULT_FIELD_POSITIONS,
  FieldPosition,
  RECIPE_FIELD_KEYS,
  RecipeFieldKey,
} from '../../components/receta/RecipePrintCanvas';
import RecipeBeneficenciaCanvas, {
  RecipeBeneficenciaFieldKey,
  DEFAULT_BENEFICENCIA_POSITIONS,
  RECIPE_BENEFICENCIA_FIELD_KEYS,
} from '../../components/receta/RecipeBeneficenciaCanvas';
import RecipeAngelesCanvas, {
  RecipeAngelesFieldKey,
  DEFAULT_ANGELES_POSITIONS,
  RECIPE_ANGELES_FIELD_KEYS,
} from '../../components/receta/RecipeAngelesCanvas';

type RecipeFormat = 'beneficencia' | 'angeles';
type AnyRecipeFieldKey = RecipeFieldKey | RecipeBeneficenciaFieldKey | RecipeAngelesFieldKey;

export default function RecetaRapidaPrintPage() {
  const navigate = useNavigate();
  const { state } = useLocation() as {
    state?: {
      nombrePaciente?: string;
      nombre?: string;
      apellido1?: string;
      edadTexto?: string;
      peso?: string;
      talla?: string;
      percentilPeso?: string;
      percentilTalla?: string;
      prescription?: string;
      fecha?: string;
      formato?: string;
      recetaToken?: string | null;
    };
  };

  const [doctorInfo, setDoctorInfo] = useState<DoctorInfo | null>(null);
  const [recipeFormat, setRecipeFormat] = useState<RecipeFormat>(() => {
    if (state?.formato === 'beneficencia' || state?.formato === 'angeles') return state.formato;
    const saved = localStorage.getItem('recipeFormat');
    return (saved === 'beneficencia' || saved === 'angeles') ? saved : 'beneficencia';
  });

  const [editMode, setEditMode] = useState(false);
  const [selectedField, setSelectedField] = useState<AnyRecipeFieldKey | null>(null);
  const [fieldPositions, setFieldPositions] = useState<Partial<Record<string, FieldPosition>>>({});
  const [initialLayout, setInitialLayout] = useState<Record<string, FieldPosition>>(
    RECIPE_FIELD_KEYS.reduce((acc, k) => ({ ...acc, [k]: { ...DEFAULT_FIELD_POSITIONS[k] } }), {} as Record<string, FieldPosition>)
  );

  useEffect(() => {
    const run = async () => {
      try {
        const doctor = await medicoVfpService.getDoctorInfo().catch(() => null);
        setDoctorInfo(doctor);
      } catch (e) {
        console.error('Error al cargar doctor:', e);
      }
    };
    run();
  }, []);

  // Cargar layout
  useEffect(() => {
    const loadLayout = async () => {
      try {
        const layouts = await pacientesService.getRecetaLayout(recipeFormat);
        const layoutMap = layouts.reduce<Partial<Record<string, FieldPosition>>>((acc, layout) => {
          acc[layout.campo] = {
            top: layout.top,
            left: layout.left,
            ...(layout.width != null ? { width: layout.width } : {}),
          };
          return acc;
        }, {});

        const allKeys = recipeFormat === 'beneficencia'
          ? RECIPE_BENEFICENCIA_FIELD_KEYS
          : recipeFormat === 'angeles'
          ? RECIPE_ANGELES_FIELD_KEYS
          : RECIPE_FIELD_KEYS;
        const defaultPositions: Record<string, FieldPosition> = recipeFormat === 'beneficencia'
          ? DEFAULT_BENEFICENCIA_POSITIONS
          : recipeFormat === 'angeles'
          ? DEFAULT_ANGELES_POSITIONS
          : DEFAULT_FIELD_POSITIONS;

        const merged = allKeys.reduce<Record<string, FieldPosition>>((acc, key) => {
          const base = defaultPositions[key];
          const override = layoutMap[key];
          acc[key] = { ...base, ...(override ?? {}) };
          return acc;
        }, {});

        // Si qrCode es campo nuevo sin posición guardada, alinearlo debajo del doctor
        if (!layoutMap['qrCode'] && merged['qrCode']) {
          if (recipeFormat === 'angeles') {
            const anchor = merged['doctorLine3'] || merged['doctorLine1'];
            if (anchor) {
              merged['qrCode'] = { top: anchor.top + 80, left: anchor.left };
            }
          } else {
            const anchor = merged['doctorName'];
            if (anchor) {
              merged['qrCode'] = { top: anchor.top + 80, left: 1300 };
            }
          }
        }

        const overrides = allKeys.reduce<Partial<Record<string, FieldPosition>>>((acc, key) => {
          const base = defaultPositions[key];
          const next = merged[key];
          if (next.top !== base.top || next.left !== base.left || (next.width ?? null) !== (base.width ?? null)) {
            acc[key] = { ...next };
          }
          return acc;
        }, {});

        setInitialLayout(merged);
        setFieldPositions(overrides);
      } catch (e) {
        console.error('Error al cargar layout:', e);
      }
    };
    loadLayout();
  }, [recipeFormat]);

  const PX_TO_CANVAS_UNITS = 2.64583333;
  const BASE_KEY_STEP = Math.max(1, Math.round(PX_TO_CANVAS_UNITS));

  const handleFieldBoundsChange = useCallback(
    () => {},
    [],
  );

  const updateFieldPosition = (field: string, updater: (current: FieldPosition) => FieldPosition) => {
    setFieldPositions((prev) => {
      const defaultPositions: Record<string, FieldPosition> = recipeFormat === 'beneficencia'
        ? DEFAULT_BENEFICENCIA_POSITIONS
        : recipeFormat === 'angeles'
        ? DEFAULT_ANGELES_POSITIONS
        : DEFAULT_FIELD_POSITIONS;
      const base = defaultPositions[field];
      if (!base) return prev;
      const current = { ...base, ...prev[field] };
      const updated = updater(current);
      const sanitized: FieldPosition = { top: Math.round(updated.top), left: Math.round(updated.left) };
      const sameAsDefault = sanitized.top === base.top && sanitized.left === base.left;
      if (sameAsDefault) {
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: sanitized };
    });
  };

  useEffect(() => {
    if (!editMode) return;
    const handler = (event: KeyboardEvent) => {
      if (!selectedField) return;
      const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      if (!arrowKeys.includes(event.key)) return;
      event.preventDefault();
      const multiplier = event.shiftKey ? 5 : 1;
      const delta = BASE_KEY_STEP * multiplier;
      updateFieldPosition(selectedField, (current) => {
        let nextTop = current.top;
        let nextLeft = current.left;
        switch (event.key) {
          case 'ArrowUp': nextTop -= delta; break;
          case 'ArrowDown': nextTop += delta; break;
          case 'ArrowLeft': nextLeft -= delta; break;
          case 'ArrowRight': nextLeft += delta; break;
        }
        return { ...current, top: nextTop, left: nextLeft };
      });
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editMode, selectedField, BASE_KEY_STEP]);

  const handleToggleEdit = () => {
    setEditMode(!editMode);
    setSelectedField(null);
  };

  const handleResetPositions = () => {
    setFieldPositions({});
    setSelectedField(null);
    toast.success('Posiciones restablecidas');
  };

  const handleSavePositions = async () => {
    try {
      const allKeys = recipeFormat === 'beneficencia'
        ? RECIPE_BENEFICENCIA_FIELD_KEYS
        : recipeFormat === 'angeles'
        ? RECIPE_ANGELES_FIELD_KEYS
        : RECIPE_FIELD_KEYS;
      const defaultPositions: Record<string, FieldPosition> = recipeFormat === 'beneficencia'
        ? DEFAULT_BENEFICENCIA_POSITIONS
        : recipeFormat === 'angeles'
        ? DEFAULT_ANGELES_POSITIONS
        : DEFAULT_FIELD_POSITIONS;

      const mergedLayout = allKeys.reduce<Record<string, FieldPosition>>((acc, key) => {
        const base = defaultPositions[key];
        const override = fieldPositions[key];
        acc[key] = { ...base, ...(override ?? {}) };
        return acc;
      }, {});

      const hasChanges = allKeys.some((key) => {
        const next = mergedLayout[key];
        const prev = initialLayout[key];
        return next.top !== prev.top || next.left !== prev.left || (next.width ?? null) !== (prev.width ?? null);
      });
      if (!hasChanges) {
        toast.error('No hay cambios para guardar');
        return;
      }
      const positions = allKeys.map((campo) => ({
        campo,
        top: mergedLayout[campo].top,
        left: mergedLayout[campo].left,
        width: mergedLayout[campo].width,
      }));
      await pacientesService.updateRecetaLayout({ formato: recipeFormat, positions });
      toast.success('Posiciones guardadas exitosamente');
      setInitialLayout(mergedLayout);
    } catch (error) {
      console.error('Error al guardar posiciones:', error);
      toast.error('Error al guardar posiciones');
    }
  };

  const handlePrint = () => {
    setEditMode(false);
    setSelectedField(null);
    window.print();
  };

  const handleFormatChange = (format: RecipeFormat) => {
    setRecipeFormat(format);
    localStorage.setItem('recipeFormat', format);
  };

  const patientName = [state?.nombre, state?.apellido1].filter(Boolean).join(' ').toUpperCase() || (state?.nombrePaciente ?? '').toUpperCase();
  const prescription = (state?.prescription ?? '').trimEnd();
  const date = state?.fecha ?? '';
  const weight = state?.peso ?? '';
  const height = state?.talla ?? '';
  const weightPercentile = state?.percentilPeso ?? '';
  const heightPercentile = state?.percentilTalla ?? '';
  const doctorName = doctorInfo?.nombre ?? 'Dr. José Luis Cadena Santillana';

  // Parse edad from "X años Y meses Z días"
  const edadMatch = state?.edadTexto?.match(/(\d+)\s*años?\s*(\d+)\s*meses?\s*(\d+)\s*d[ií]as?/i);
  const ageYears = edadMatch ? edadMatch[1] : '';
  const ageMonths = edadMatch ? edadMatch[2] : '';
  const ageDays = edadMatch ? edadMatch[3] : '';

  return (
    <div className="max-w-6xl mx-auto print:max-w-none print:mx-0">
      {/* Controles */}
      <div className="flex flex-wrap gap-3 justify-between items-center mb-6 print:hidden">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/receta-rapida')}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Volver
          </button>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Formato:</label>
            <select
              value={recipeFormat}
              onChange={(e) => handleFormatChange(e.target.value as RecipeFormat)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="beneficencia">Beneficencia Española</option>
              <option value="angeles">Hospital Ángeles</option>
            </select>
          </div>

          <button
            onClick={handleToggleEdit}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors border ${
              editMode
                ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                : 'text-blue-700 border-blue-200 hover:bg-blue-50'
            }`}
          >
            {editMode ? 'Salir modo edición' : 'Editar posiciones'}
          </button>
          {editMode && (
            <>
              <button
                onClick={handleSavePositions}
                className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                Guardar posiciones
              </button>
              <button
                onClick={handleResetPositions}
                className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                Restablecer posiciones
              </button>
            </>
          )}
        </div>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <PrinterIcon className="w-5 h-5" />
          Imprimir Receta
        </button>
      </div>

      {/* Vista previa */}
      <div className="print:hidden">
        {editMode && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-900 px-4 py-3 text-sm space-y-1">
            <p className="font-semibold text-blue-900">Modo edición activo</p>
            <p>Selecciona un campo y usa las flechas para moverlo 1px.</p>
            <p>Mantén Shift para mover de 5px en 5px. Pulsa "Restablecer posiciones" para volver al diseño original.</p>
          </div>
        )}
        <div className="recipe-preview-shell">
          {recipeFormat === 'beneficencia' ? (
            <RecipeBeneficenciaCanvas
              patientName={patientName}
              ageYears={ageYears}
              ageMonths={ageMonths}
              ageDays={ageDays}
              weight={weight}
              weightPercentile={weightPercentile}
              height={height}
              heightPercentile={heightPercentile}
              prescription={prescription}
              doctorName={doctorName}
              consultDate={date}
              recetaToken={state?.recetaToken}
              editMode={editMode}
              selectedField={selectedField as any}
              onSelectField={setSelectedField as any}
              fieldPositions={fieldPositions as any}
              onFieldBoundsChange={handleFieldBoundsChange as any}
            />
          ) : (
            <RecipeAngelesCanvas
              patientName={patientName}
              ageYears={ageYears}
              ageMonths={ageMonths}
              ageDays={ageDays}
              weight={weight}
              weightPercentile={weightPercentile}
              height={height}
              heightPercentile={heightPercentile}
              prescription={prescription}
              consultDate={date}
              recetaToken={state?.recetaToken}
              editMode={editMode}
              selectedField={selectedField as any}
              onSelectField={setSelectedField as any}
              fieldPositions={fieldPositions as any}
              onFieldBoundsChange={handleFieldBoundsChange as any}
            />
          )}
        </div>
      </div>

      {/* Versión para impresión */}
      <div className="hidden print:block">
        {recipeFormat === 'beneficencia' ? (
          <RecipeBeneficenciaCanvas
            patientName={patientName}
            ageYears={ageYears}
            ageMonths={ageMonths}
            ageDays={ageDays}
            weight={weight}
            weightPercentile={weightPercentile}
            height={height}
            heightPercentile={heightPercentile}
            prescription={prescription}
            doctorName={doctorName}
            consultDate={date}
            recetaToken={state?.recetaToken}
            fieldPositions={fieldPositions as any}
            onFieldBoundsChange={handleFieldBoundsChange as any}
          />
        ) : (
          <RecipeAngelesCanvas
            patientName={patientName}
            ageYears={ageYears}
            ageMonths={ageMonths}
            ageDays={ageDays}
            weight={weight}
            weightPercentile={weightPercentile}
            height={height}
            heightPercentile={heightPercentile}
            prescription={prescription}
            consultDate={date}
            recetaToken={state?.recetaToken}
            fieldPositions={fieldPositions as any}
            onFieldBoundsChange={handleFieldBoundsChange as any}
          />
        )}
      </div>
    </div>
  );
}
